'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Keep secondary hero images out of the initial request queue, not just native-lazy. */
export function HeroSlideImage({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (enabled || !ref.current) return;
    const element = ref.current;
    const carousel = element.closest('.ea-carousel');
    const activate = () => setEnabled(true);
    // Prepare all slides on genuine carousel intent, including keyboard controls.
    carousel?.addEventListener('pointerdown', activate, { once: true, passive: true });
    carousel?.addEventListener('focusin', activate, { once: true });
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            entries => {
              if (entries.some(entry => entry.isIntersecting)) activate();
            },
            { rootMargin: '200px 0px', threshold: 0.01 },
          );
    if (observer) observer.observe(element);
    else activate();
    return () => {
      observer?.disconnect();
      carousel?.removeEventListener('pointerdown', activate);
      carousel?.removeEventListener('focusin', activate);
    };
  }, [enabled]);

  return (
    <span ref={ref} className="absolute inset-0 block bg-primary/10">
      {enabled ? children : <noscript>{children}</noscript>}
    </span>
  );
}
